import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Sparkles, X, ChevronDown, ChevronUp, Bell } from 'lucide-react';
import { Button } from '../ui/button';
import { useApp } from '../../context/AppContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// localStorage key — bumping the schema version forces a one-time re-trigger
// for everyone (admins only). Latest entry id stored against admin's user id.
const STORAGE_KEY = 'stt:whats-new:v1:lastSeenId';
const STORAGE_KEY_DISMISSED = 'stt:whats-new:v1:dismissed-ids';

const TAG_STYLES = {
  Feature: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Fix: 'bg-rose-100 text-rose-700 border-rose-200',
  Perf: 'bg-amber-100 text-amber-700 border-amber-200',
  Admin: 'bg-blue-100 text-blue-700 border-blue-200',
  UI: 'bg-purple-100 text-purple-700 border-purple-200',
};

/**
 * "What's New" popover. Visible only to admins (or business_head — anyone with
 * role-level <= 1). Shows a non-intrusive bell icon in the header until clicked,
 * with a red dot indicating unseen entries. Auto-opens once per browser per
 * release whenever the latest entry's id is newer than what's stored locally.
 *
 * Entries are fetched from `GET /api/whats-new` (DB-backed) so admins can manage
 * the changelog from the admin panel without redeploying.
 */
export const WhatsNewPopover = () => {
  const { currentUser, roleLevel } = useApp();
  const isAdminLike = (roleLevel ?? 99) <= 1; // admin or business_head
  const [open, setOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState({});
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEntries = useCallback(async () => {
    if (!isAdminLike) return;
    try {
      const res = await axios.get(`${API}/whats-new`, { timeout: 8000 });
      setEntries(Array.isArray(res.data?.entries) ? res.data.entries : []);
    } catch (e) {
      // Soft-fail; popover just won't show entries.
      console.warn('whats-new fetch failed', e?.message || e);
    } finally {
      setLoading(false);
    }
  }, [isAdminLike]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // Listen for admin-edit events so the popover refreshes immediately after
  // the manager tab creates/updates/deletes an entry.
  useEffect(() => {
    const handler = () => fetchEntries();
    window.addEventListener('whats-new:refresh', handler);
    return () => window.removeEventListener('whats-new:refresh', handler);
  }, [fetchEntries]);

  const sortedEntries = useMemo(
    () => [...entries].sort((a, b) => (a.date < b.date ? 1 : -1)),
    [entries]
  );
  const latestId = sortedEntries[0]?.id;

  // Track unseen entries: anything whose id isn't in dismissed list AND date is
  // newer than the last seen marker.
  const dismissedIds = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY_DISMISSED) || '[]'); }
    catch { return []; }
  }, [open]); // re-read on open so dismissals reflect immediately

  const lastSeenId = (typeof window !== 'undefined') ? localStorage.getItem(STORAGE_KEY) : null;
  const hasUnseen = isAdminLike && latestId && lastSeenId !== latestId;

  // Auto-open once per release for admin-like users
  useEffect(() => {
    if (!isAdminLike || !latestId) return;
    if (lastSeenId !== latestId) {
      // small delay so it doesn't fight the page-load animation
      const t = setTimeout(() => setOpen(true), 800);
      return () => clearTimeout(t);
    }
  }, [isAdminLike, latestId, lastSeenId, currentUser?.id]);

  if (!isAdminLike) return null;

  const markAllSeen = () => {
    try { localStorage.setItem(STORAGE_KEY, latestId); } catch {}
  };

  const handleClose = () => {
    markAllSeen();
    setOpen(false);
  };

  const dismissEntry = (id) => {
    const next = Array.from(new Set([...dismissedIds, id]));
    try { localStorage.setItem(STORAGE_KEY_DISMISSED, JSON.stringify(next)); } catch {}
    setExpandedIds(prev => ({ ...prev, [id]: false }));
  };

  const toggleEntry = (id) => setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <>
      {/* Trigger button — fits in the top-bar */}
      <button
        data-testid="whats-new-trigger"
        onClick={() => setOpen(o => !o)}
        className="relative w-9 h-9 flex items-center justify-center rounded-lg hover:bg-amber-50 transition-colors"
        title="What's new"
      >
        <Bell className="w-4 h-4 text-slate-600" />
        {hasUnseen && (
          <span data-testid="whats-new-dot" className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-rose-500 ring-2 ring-white animate-pulse" />
        )}
      </button>

      {open && (
        <>
          {/* backdrop */}
          <div
            data-testid="whats-new-backdrop"
            onClick={handleClose}
            className="fixed inset-0 bg-black/20 z-[60]"
          />

          {/* panel */}
          <div
            data-testid="whats-new-panel"
            className="fixed top-16 right-4 w-[380px] max-w-[calc(100vw-2rem)] max-h-[80vh] bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden z-[61] flex flex-col"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-gradient-to-br from-amber-50 to-white">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-600" />
                <h3 className="text-sm font-bold text-slate-900">What's new</h3>
              </div>
              <button
                data-testid="whats-new-close"
                onClick={handleClose}
                className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-100"
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 divide-y divide-slate-100">
              {sortedEntries.map(entry => {
                const expanded = !!expandedIds[entry.id];
                const isDismissed = dismissedIds.includes(entry.id);
                return (
                  <div
                    key={entry.id}
                    data-testid={`whats-new-entry-${entry.id}`}
                    className={`px-4 py-3 transition-opacity ${isDismissed ? 'opacity-50' : ''}`}
                  >
                    <button
                      onClick={() => toggleEntry(entry.id)}
                      className="w-full flex items-start gap-2 text-left"
                    >
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border flex-shrink-0 mt-0.5 ${TAG_STYLES[entry.tag] || TAG_STYLES.Feature}`}>
                        {entry.tag}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 leading-tight">{entry.title}</p>
                        <p className="text-[11px] text-slate-400 mt-0.5">{entry.date}</p>
                      </div>
                      {expanded ? <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" /> : <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />}
                    </button>

                    {expanded && (
                      <div data-testid={`whats-new-entry-body-${entry.id}`} className="mt-2 ml-12 space-y-1">
                        <ul className="text-xs text-slate-600 leading-relaxed space-y-1 list-disc list-outside pl-4">
                          {entry.body.map((line, i) => <li key={i}>{line}</li>)}
                        </ul>
                        {!isDismissed && (
                          <button
                            data-testid={`whats-new-entry-dismiss-${entry.id}`}
                            onClick={() => dismissEntry(entry.id)}
                            className="mt-2 text-[11px] text-slate-400 hover:text-slate-600 font-medium"
                          >
                            Hide this
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {sortedEntries.length === 0 && (
                <p className="text-xs text-slate-400 text-center py-8">
                  {loading ? 'Loading…' : "Nothing new — you're all caught up."}
                </p>
              )}
            </div>

            <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
              <p className="text-[11px] text-slate-500">Shown to admins after every deploy.</p>
              <Button
                data-testid="whats-new-mark-seen"
                size="sm"
                variant="outline"
                onClick={handleClose}
                className="h-7 text-xs"
              >
                Got it
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default WhatsNewPopover;
